import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Line } from "@react-three/drei";
import * as THREE from "three";

const R = 1.15;
const COUNT = 2600;

const HUBS = [
  [7.06, 171.38], [1.35, 103.8], [51.5, 0.12], [25.2, 55.3],
  [35.6, 139.7], [-23.5, -46.6], [40.7, -74.0], [6.5, 3.4],
  [22.3, 114.2], [48.8, 2.3], [-33.9, 151.2], [19.1, 72.9],
  [31.2, 121.5], [-1.3, 36.8],
];

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const smooth = (a, b, v) => {
  const t = clamp01((v - a) / (b - a));
  return t * t * (3 - 2 * t);
};

function ll(lat, lon, radius) {
  const phi = ((90 - lat) * Math.PI) / 180;
  const theta = ((lon + 180) * Math.PI) / 180;
  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta)
  );
}

export function Globe({ progressRef }) {
  const group = useRef(null);
  const ptsRef = useRef(null);
  const ptsMat = useRef(null);
  const arcMats = useRef([]);
  const nodeRefs = useRef([]);
  const wireMat = useRef(null);

  const { cloud, sphere, positions } = useMemo(() => {
    const cloudArr = new Float32Array(COUNT * 3);
    const sphereArr = new Float32Array(COUNT * 3);
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < COUNT; i += 1) {
      const th = Math.random() * Math.PI * 2;
      const ph = Math.acos(2 * Math.random() - 1);
      const rr = 1.9 + Math.random() * 2.3;
      cloudArr[i * 3] = rr * Math.sin(ph) * Math.cos(th);
      cloudArr[i * 3 + 1] = rr * Math.cos(ph);
      cloudArr[i * 3 + 2] = rr * Math.sin(ph) * Math.sin(th);
      const y = 1 - (i / (COUNT - 1)) * 2;
      const rad = Math.sqrt(1 - y * y);
      const gt = golden * i;
      sphereArr[i * 3] = Math.cos(gt) * rad * R;
      sphereArr[i * 3 + 1] = y * R;
      sphereArr[i * 3 + 2] = Math.sin(gt) * rad * R;
    }
    return { cloud: cloudArr, sphere: sphereArr, positions: new Float32Array(cloudArr) };
  }, []);

  const arcs = useMemo(
    () =>
      HUBS.map((h, i) => {
        const next = HUBS[(i + 4) % HUBS.length];
        const a = ll(h[0], h[1], R);
        const b = ll(next[0], next[1], R);
        const mid = a
          .clone()
          .add(b)
          .multiplyScalar(0.5)
          .normalize()
          .multiplyScalar(R * (1.25 + a.distanceTo(b) * 0.12));
        return new THREE.QuadraticBezierCurve3(a, mid, b).getPoints(40);
      }),
    []
  );
  const nodePositions = useMemo(() => HUBS.map((h) => ll(h[0], h[1], R * 1.004)), []);
  const cGrey = useMemo(() => new THREE.Color("#6d6d74"), []);
  const cWarm = useMemo(() => new THREE.Color("#c9c9ce"), []);

  useFrame((state) => {
    const p = progressRef.current || 0;
    const t = state.clock.elapsedTime;
    const morph = smooth(0.45, 0.575, p);
    const vis = smooth(0.355, 0.42, p);
    const travel = smooth(0.84, 0.97, p);

    if (ptsRef.current && ptsMat.current) {
      const attr = ptsRef.current.geometry.attributes.position;
      const arr = attr.array;
      const driftAmt = 0.006 * (1 - morph);
      for (let i = 0; i < COUNT; i += 1) {
        const ix = i * 3;
        arr[ix] = cloud[ix] + (sphere[ix] - cloud[ix]) * morph + Math.sin(t * 0.5 + i) * driftAmt;
        arr[ix + 1] = cloud[ix + 1] + (sphere[ix + 1] - cloud[ix + 1]) * morph + Math.cos(t * 0.4 + i * 1.3) * driftAmt;
        arr[ix + 2] = cloud[ix + 2] + (sphere[ix + 2] - cloud[ix + 2]) * morph;
      }
      attr.needsUpdate = true;
      ptsMat.current.opacity = 0.85 * vis * (1 - travel * 0.55);
      ptsMat.current.color.lerpColors(cGrey, cWarm, morph * 0.7);
    }

    if (group.current) {
      group.current.rotation.y = t * 0.03 + p * Math.PI * 2.2;
      group.current.rotation.x = 0.28 - p * 0.2 + travel * 0.85;
      group.current.scale.y = 1 - travel * 0.82;
      group.current.scale.z = 1 + travel * 0.9;
    }

    const cam = state.camera;
    const cz = THREE.MathUtils.lerp(3.8, 2.35, smooth(0.46, 0.78, p));
    cam.position.z = THREE.MathUtils.lerp(cz, 1.25, travel);
    cam.position.y = THREE.MathUtils.lerp(0.4, -0.05, travel);

    const intensify = 1 + smooth(0.66, 0.78, p) * 0.9;
    if (wireMat.current) {
      wireMat.current.opacity = 0.055 * morph * (1 - travel * 0.4);
    }
    arcMats.current.forEach((m, i) => {
      if (m) {
        m.opacity = Math.min(0.9, smooth(0.575 + i * 0.006, 0.625 + i * 0.006, p) * 0.7 * intensify);
      }
    });
    nodeRefs.current.forEach((m, i) => {
      if (m) {
        const s = smooth(0.6 + (i % 7) * 0.012, 0.68 + (i % 7) * 0.012, p) * 0.028;
        m.scale.setScalar(Math.max(s, 0.0001));
      }
    });
  });

  return (
    <group ref={group}>
      <points ref={ptsRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        </bufferGeometry>
        <pointsMaterial
          ref={ptsMat}
          size={0.014}
          sizeAttenuation
          transparent
          opacity={0}
          depthWrite={false}
        />
      </points>
      <mesh>
        <sphereGeometry args={[R * 0.985, 28, 28]} />
        <meshBasicMaterial ref={wireMat} wireframe transparent opacity={0} color="#ffffff" depthWrite={false} />
      </mesh>
      {arcs.map((pts, i) => (
        <Line
          key={`arc-${i}`}
          points={pts}
          color="#FF5A00"
          lineWidth={1}
          transparent
          opacity={0}
          ref={(el) => {
            if (el) arcMats.current[i] = el.material;
          }}
        />
      ))}
      {nodePositions.map((pos, i) => (
        <mesh
          key={`node-${i}`}
          position={pos}
          ref={(el) => {
            if (el) nodeRefs.current[i] = el;
          }}
        >
          <sphereGeometry args={[1, 12, 12]} />
          <meshBasicMaterial color="#FF5A00" />
        </mesh>
      ))}
    </group>
  );
}
